import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../utils/config';
import { Link, useSearchParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';

export default function CreditNoteList() {
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(Number(sp.get('page') || 1));
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sums, setSums] = useState({});

  // Filters state
  const [customer, setCustomer] = useState(null);
  const [dateFrom, setDateFrom] = useState(sp.get('date_from') || '');
  const [dateTo, setDateTo] = useState(sp.get('date_to') || '');
  const [q, setQ] = useState(sp.get('q') || '');
  const [minAmt, setMinAmt] = useState(sp.get('min_amount') || '');
  const [maxAmt, setMaxAmt] = useState(sp.get('max_amount') || '');

  const token = localStorage.getItem("token");

const fetchCustomers = async (inputValue) => {
  const r = await axios.get(`${API_BASE_URL}/api/customers`, {
    params: { q: inputValue, limit: 20 },
    headers: { Authorization: `Bearer ${token}` },
  });
return (r.data || []).map(c => ({ value: c.id, label: c.name }));

};

const load = async (pageOverride) => {
  setLoading(true);
  try {
    const params = {
      page: pageOverride || page,
      page_size: pageSize,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      q: q || undefined,
      min_amount: minAmt || undefined,
      max_amount: maxAmt || undefined,
      customer_id: customer?.value || undefined,
      sort_by: 'credit_note_date',
      sort_dir: 'desc',
    };
    const r = await axios.get(`${API_BASE_URL}/api/credit`, {
      params,
      headers: { Authorization: `Bearer ${token}` }, // ✅ Add this
    });
    setRows(r.data.rows || []);
    setTotal(r.data.total || 0);
    setSums(r.data.sums || {});
    setPage(r.data.page || 1);

    const spNew = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v) spNew.set(k, String(v));
    });
    setSp(spNew, { replace: true });
  } finally {
    setLoading(false);
  }
};


  useEffect(() => { load(page); /* eslint-disable-next-line */ }, []);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Credit Notes</h1>
        <Link to="/credit/new" className="px-3 py-2 rounded-xl shadow bg-blue-600 text-white">+ New Credit Note</Link>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3 bg-white p-3 rounded-xl shadow">
        <div className="md:col-span-2">
          <label className="text-xs text-gray-500">Customer</label>
          <AsyncSelect cacheOptions defaultOptions loadOptions={fetchCustomers} value={customer} onChange={setCustomer} />
        </div>
        <div>
          <label className="text-xs text-gray-500">From</label>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} className="w-full border rounded-lg px-2 py-2" />
        </div>
        <div>
          <label className="text-xs text-gray-500">To</label>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} className="w-full border rounded-lg px-2 py-2" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Search (remarks/invoice#)</label>
          <input value={q} onChange={e=>setQ(e.target.value)} className="w-full border rounded-lg px-2 py-2" placeholder="text..." />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500">Min Amt</label>
            <input type="number" value={minAmt} onChange={e=>setMinAmt(e.target.value)} className="w-full border rounded-lg px-2 py-2" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Max Amt</label>
            <input type="number" value={maxAmt} onChange={e=>setMaxAmt(e.target.value)} className="w-full border rounded-lg px-2 py-2" />
          </div>
        </div>
        <div className="md:col-span-6 flex gap-2 justify-end">
          <button onClick={()=>load(1)} className="px-3 py-2 rounded-lg bg-gray-800 text-white">Apply</button>
          <button onClick={()=>{setCustomer(null);setDateFrom('');setDateTo('');setQ('');setMinAmt('');setMaxAmt('');load(1);}} className="px-3 py-2 rounded-lg bg-gray-200">Reset</button>
        </div>
      </div>

      {/* Sums */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat title="Taxable" value={sums?.sum_taxable} />
        <Stat title="GST" value={sums?.sum_gst} />
        <Stat title="Deduction" value={sums?.sum_deduction} />
        <Stat title="Credit Note Amount" value={sums?.sum_credit_note_amount} />
      </div>

      <div className="overflow-auto bg-white rounded-xl shadow">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              <Th>ID</Th>
              <Th>Date</Th>
              <Th>Customer</Th>
              <Th className="text-right">Taxable</Th>
              <Th className="text-right">GST</Th>
              <Th className="text-right">Deduction</Th>
              <Th className="text-right">CN Amount</Th>
              <Th>Items</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-4">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-4">No results</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t">
                <td className="p-3 font-mono">CN-{r.id}</td>
                <td className="p-3">{r.credit_note_date ? new Date(r.credit_note_date).toLocaleDateString() : '-'}</td>
                <td className="p-3">{r.customer_name}</td>
                <td className="p-3 text-right">{Number(r.total_amount || 0).toFixed(2)}</td>
                <td className="p-3 text-right">{Number(r.gst_total || 0).toFixed(2)}</td>
                <td className="p-3 text-right">{Number(r.deduction || 0).toFixed(2)}</td>
                <td className="p-3 text-right font-semibold">{Number(r.credit_note_amount || 0).toFixed(2)}</td>
                <td className="p-3">{r.items_count}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Link to={`/credit/${r.id}`} className="px-2 py-1 border rounded">View</Link>
                    <Link to={`/credit/${r.id}/edit`} className="px-2 py-1 border rounded">Edit</Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 justify-end">
        <button disabled={page<=1} onClick={()=>{setPage(p=>p-1); load(page-1);}} className="px-3 py-2 rounded border">Prev</button>
        <span className="text-sm">Page {page} / {totalPages}</span>
        <button disabled={page>=totalPages} onClick={()=>{setPage(p=>p+1); load(page+1);}} className="px-3 py-2 rounded border">Next</button>
      </div>
    </div>
  );
}

function Th({ children, className='' }) {
  return <th className={`p-3 text-xs font-semibold text-gray-600 ${className}`}>{children}</th>;
}
function Stat({ title, value }) {
  return (
    <div className="bg-white rounded-xl shadow p-3">
      <div className="text-xs text-gray-500">{title}</div>
      <div className="text-lg font-semibold">₹ {Number(value||0).toFixed(2)}</div>
    </div>
  );
}